import { useRef, useEffect } from "react";

export function LoadMoreSentinel({
  onLoadMore,
  isLoading,
  hasMore,
  testId = "load-more-sentinel",
}: {
  onLoadMore: () => void;
  isLoading: boolean;
  hasMore: boolean;
  testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Always-fresh values — no stale closures in scroll handler
  const stateRef = useRef({ onLoadMore, isLoading, hasMore });
  stateRef.current = { onLoadMore, isLoading, hasMore };
  // Stable check fn shared between scroll handler and post-load effect
  const checkRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Walk up the DOM to find the nearest scrollable ancestor
    let container: HTMLElement | null = null;
    let node: HTMLElement | null = el.parentElement;
    while (node) {
      const oy = window.getComputedStyle(node).overflowY;
      if (oy === "auto" || oy === "scroll") {
        container = node;
        break;
      }
      node = node.parentElement;
    }

    const check = () => {
      const { onLoadMore, isLoading, hasMore } = stateRef.current;
      if (isLoading || !hasMore) return;
      const remaining = container
        ? container.scrollHeight - container.scrollTop - container.clientHeight
        : document.documentElement.scrollHeight -
          window.scrollY -
          window.innerHeight;
      if (remaining < 300) onLoadMore();
    };

    checkRef.current = check;
    const target: EventTarget = container ?? window;
    target.addEventListener("scroll", check as EventListener, {
      passive: true,
    });
    return () => target.removeEventListener("scroll", check as EventListener);
  }, []); // runs once — scroll listener stays for component's lifetime

  // After each load finishes, re-check if still near the bottom
  useEffect(() => {
    if (!isLoading) checkRef.current?.();
  }, [isLoading]);

  return (
    <div ref={ref} className="scd-load-more-sentinel" data-testid={testId}>
      {isLoading && hasMore && (
        <span className="scd-load-more-sentinel__text">Загрузка...</span>
      )}
    </div>
  );
}
